import type { TFunction } from "i18next";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

/**
 * Shared "time remaining" formatting for campaign cards/badges — weeks/days as
 * plain text, an hh : mm / mm : ss ticker below a day. Lives outside the badge
 * component file so non-component exports don't break react-refresh.
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;

export function parseEndDate(end: string): Date {
  return new Date(end);
}

/** Two-digit unit, so "5s" reads "05s" and the ticker keeps a fixed width. */
const pad = (value: number): string => String(value).padStart(2, "0");

/**
 * Ticking `getTimeRemaining`. The bare function only recomputes when its caller
 * happens to re-render, so a card's `mm : ss` sat frozen between stream pushes
 * — use this wherever the value is displayed.
 *
 * The cadence follows what the string actually shows: per second only once it
 * is counting seconds (< 1h), per minute above that, and never once ended — a
 * feed of cards reading "3 days" must not re-render every second to redraw a
 * number that changes hourly. Rescheduling after each tick (rather than a fixed
 * interval) is what lets `hh : mm` switch itself to `mm : ss` on the hour.
 *
 * `active` keeps the timer off for callers that render no ticker at all (the
 * title-only badge); the hook itself still runs unconditionally.
 */
export function useTimeRemaining(
  endDate: Date,
  t: TFunction,
  active = true,
  overrun = false
): React.ReactNode {
  const endMs = endDate.getTime();
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!active || !Number.isFinite(endMs)) return;
    let id: number | undefined;
    const schedule = () => {
      const remaining = endMs - Date.now();
      // Ended: the tick that got us here already rendered "Ended", and nothing
      // below changes again.
      if (remaining <= 0) return;
      id = window.setTimeout(
        () => {
          setTick((n) => n + 1);
          schedule();
        },
        remaining < HOUR ? SECOND : MINUTE
      );
    };
    schedule();
    return () => window.clearTimeout(id);
  }, [endMs, active]);

  return getTimeRemaining(endDate, t, overrun);
}

/** `overrun`: past the end, show a zero clock instead of "Ended" — for a campaign the server
 *  still lists as ACTIVE (its end passed, the finishing tick has not run yet); only a FINISHED
 *  status may say "Ended", or the feed shows an "Ended" card under "Active". */
export const getTimeRemaining = (
  endDate: Date,
  t: TFunction,
  overrun = false
): React.ReactNode => {
  const TimeColonComponent = () => {
    return (
      <motion.div className="animate-caret-blink motion-reduce:animate-none">
        :
      </motion.div>
    );
  };
  const now = new Date();
  const raw = endDate.getTime() - now.getTime();

  if (raw <= 0 && !overrun) {
    return t("campaign.ended");
  }
  const timeRemaining = Math.max(0, raw);

  const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

  if (days >= 7) {
    return t("countdown.week", { count: Math.floor(days / 7) });
  }
  if (timeRemaining >= 24 * 60 * 60 * 1000) {
    return t("countdown.day", { count: days });
  }
  // Zero-padded + tabular figures: every digit is the same width and the unit
  // count never drops a character, so a ticker counting down can't jiggle its
  // own width (and shove the layout around it) once a second.
  if (timeRemaining >= 60 * 60 * 1000) {
    return (
      <div className="flex items-center gap-0.5 tabular-nums">
        {pad(hours)}h <TimeColonComponent /> {pad(minutes)}m
      </div>
    );
  }
  return (
    <div className="flex items-center gap-0.5 tabular-nums">
      {pad(minutes)}m <TimeColonComponent /> {pad(seconds)}s
    </div>
  );
};
